import { useEffect, useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import type { FillSource } from '@/features/reports/lib/order-proposal-math'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'

const SOURCES: Array<FillSource> = ['sum', 'avg']

/**
 * "Bestellung füllen" (D8): an uplift, Übernehmen and Leeren. Only rendered with the order
 * permission. For a range it also offers Σ or Ø as the source (CHG-2026-10-bestellvorschlag-simplify,
 * D2); a single day always fills from Verkauf.
 */
export function FillBar({
  source,
  onSourceChange,
  upliftPercent,
  onUpliftChange,
  isRange,
  onFill,
  onClear,
}: {
  source: FillSource
  onSourceChange: (source: FillSource) => void
  upliftPercent: number
  onUpliftChange: (percent: number) => void
  isRange: boolean
  onFill: () => void
  onClear: () => void
}) {
  const { t } = useTranslation('reports')
  // The field holds text while it is typed — an emptied field is not 0 until it is left that way.
  const [uplift, setUplift] = useState(String(upliftPercent))
  useEffect(() => setUplift(String(upliftPercent)), [upliftPercent])

  return (
    <div
      className="flex shrink-0 flex-wrap items-center justify-end gap-2 text-sm"
      data-print-hide
    >
      <span className="font-semibold">{t('orderProposal.fill')}</span>
      {isRange ? (
        <ToggleGroup
          type="single"
          variant="outline"
          value={source}
          onValueChange={(next) => {
            if (next) onSourceChange(next as FillSource)
          }}
          aria-label={t('orderProposal.fill')}
        >
          {SOURCES.map((item) => (
            <ToggleGroupItem key={item} value={item} className="h-8 px-3">
              {t(`orderProposal.fillSources.${item}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      ) : null}
      <span className="flex items-center gap-1">
        +
        <Input
          type="number"
          min={0}
          max={200}
          step={5}
          inputMode="numeric"
          value={uplift}
          onChange={(event) => {
            setUplift(event.target.value)
            const value = Number(event.target.value)
            if (event.target.value !== '' && Number.isFinite(value)) {
              onUpliftChange(Math.max(0, value))
            }
          }}
          onBlur={() => setUplift(String(upliftPercent))}
          aria-label={t('orderProposal.uplift')}
          className="h-8 w-16 text-right tabular-nums"
        />
        %
      </span>
      <Button size="sm" className="h-8" onClick={onFill}>
        {t('orderProposal.apply')}
      </Button>
      <Button variant="link" size="sm" className="h-8 px-1" onClick={onClear}>
        {t('orderProposal.clear')}
      </Button>
    </div>
  )
}
